import { useEffect, useRef } from 'react';

/**
 * Listen for hardware barcode scanners that emit keystrokes into the active
 * window (Zebra DataWedge "Keystroke output" mode, Honeywell HID profile,
 * most USB 1D/2D scanners in HID mode).
 *
 * Design choices, learned the hard way on physical Zebra TC27 handhelds:
 *
 *   1. Listen on `window` in capture phase. DataWedge injects keystrokes at
 *      the focused element; if focus is on <body> (common in PWAs) and we
 *      bind to an input we miss them. Window-level keydown catches every
 *      scan no matter where the focus is.
 *
 *   2. Trust the Enter suffix unconditionally. DataWedge appends a CR/LF
 *      by default (the "ENTER" action key) at the end of every scan. When
 *      we see one we flush whatever's in the buffer, without checking the
 *      inter-key timing. The user physically cannot type a fast-enough
 *      burst followed by Enter to fake a scan, so this is safe.
 *
 *   3. Inter-key threshold applies ONLY to idle-timeout flushes (i.e. the
 *      scanner is not configured to append Enter). Humans type ≥ 100 ms
 *      between keys; scanner bursts on the TC27 WebView arrive at 30-90 ms
 *      between events. We use a 130 ms ceiling — generous enough for slow
 *      rugged handhelds but well below human typing speed.
 *
 *   4. Single global handler. We expose the buffer to the caller via
 *      `onProgress` so the UI can show "Letto: X chars…" diagnostics —
 *      essential for field operators to verify DataWedge is configured
 *      correctly without round-tripping to backend.
 *
 * The hook is a no-op while `enabled` is false.
 */
export function useHardwareScanner({ enabled, onScan, onProgress, minLength = 3 }) {
  // Refs keep state across renders without re-triggering effects.
  const bufferRef = useRef('');
  const timestampsRef = useRef([]);
  const flushTimerRef = useRef(null);

  // Latest callbacks via refs so we don't re-attach the listener every render.
  const onScanRef = useRef(onScan);
  const onProgressRef = useRef(onProgress);
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);
  useEffect(() => { onProgressRef.current = onProgress; }, [onProgress]);

  useEffect(() => {
    if (!enabled) return undefined;

    const reset = (notify = true) => {
      bufferRef.current = '';
      timestampsRef.current = [];
      if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null; }
      if (notify && onProgressRef.current) {
        try { onProgressRef.current(''); } catch (e) { /* ignore */ }
      }
    };

    // `viaEnter=true` means DataWedge appended its Enter suffix — we trust it
    // unconditionally. `viaEnter=false` means we hit the idle timeout, so we
    // apply the speed threshold to reject accidental human typing.
    const flush = (viaEnter) => {
      const buf = bufferRef.current;
      const ts = timestampsRef.current;
      reset(false); // we already have buf in a local
      if (buf.length < minLength) {
        if (onProgressRef.current) { try { onProgressRef.current(''); } catch (e) { /* ignore */ } }
        return;
      }
      if (!viaEnter && ts.length >= 4) {
        const deltas = [];
        for (let i = 1; i < ts.length; i += 1) deltas.push(ts[i] - ts[i - 1]);
        const avg = deltas.reduce((a, b) => a + b, 0) / deltas.length;
        if (avg > 130) {
          if (onProgressRef.current) { try { onProgressRef.current(''); } catch (e) { /* ignore */ } }
          return;
        }
      }
      // The buffer may contain a tracking URL emitted by the scanner verbatim.
      let code = buf;
      try {
        const u = new URL(buf);
        const m = (u.pathname || '').match(/track\/([^/]+)$/);
        if (m) code = decodeURIComponent(m[1]);
        else if (u.searchParams.get('code')) code = u.searchParams.get('code');
      } catch (e) { /* not a URL, raw code */ }
      try { if (navigator.vibrate) navigator.vibrate(60); } catch (e) { /* haptics opt-in */ }
      if (onProgressRef.current) { try { onProgressRef.current(''); } catch (e) { /* ignore */ } }
      if (onScanRef.current) onScanRef.current(code);
    };

    const handler = (ev) => {
      // Ignore key combinations (modifier keys) so Cmd+R etc. work normally.
      if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
      // Enter (CR/LF) triggers an immediate flush — DataWedge's default suffix.
      if (ev.key === 'Enter' || ev.key === 'Tab') {
        if (bufferRef.current.length >= minLength) {
          ev.preventDefault();
          ev.stopPropagation();
          flush(true);
        } else {
          reset();
        }
        return;
      }
      // Only buffer single printable characters (letters, digits, punctuation).
      if (ev.key.length !== 1) return;
      const now = Date.now();
      const ts = timestampsRef.current;
      bufferRef.current += ev.key;
      ts.push(now);
      if (onProgressRef.current) {
        try { onProgressRef.current(bufferRef.current); } catch (e) { /* ignore */ }
      }
      // Auto-flush after a short idle period (covers scanners that don't
      // append a terminator). 180 ms tolerates slow rugged handhelds.
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(() => flush(false), 180);
      // Suppress the keystroke from reaching the page so it doesn't pollute
      // any focused input with the scanned code (the page should use the
      // `onScan` callback instead of reading from its inputs).
      if (bufferRef.current.length > 2) {
        ev.preventDefault();
        ev.stopPropagation();
      }
    };

    window.addEventListener('keydown', handler, true);
    return () => {
      window.removeEventListener('keydown', handler, true);
      reset(false);
    };
  }, [enabled, minLength]);
}

/**
 * Fallback parser for scanners that inject the full barcode in a single
 * `input` event on the focused element (some DataWedge keystroke variants
 * batch the string instead of dispatching individual keydowns, especially
 * with non-ASCII suffixes).
 *
 * Call this from the hidden input's `onChange` / `onInput` handler. The
 * caller is responsible for clearing the input value after we fire onScan
 * (we don't mutate the DOM directly here, to keep the hook pure).
 *
 * `value` is the current input value. We return the parsed code if it
 * looks like a scan (length >= minLength and contains no whitespace), or
 * null to indicate "not a scan, keep accumulating".
 */
export function parseScannerInput(value, { minLength = 3 } = {}) {
  if (!value) return null;
  const trimmed = value.replace(/[\r\n\t]/g, '').trim();
  if (trimmed.length < minLength) return null;
  // Try to extract the tracking code from a URL.
  let code = trimmed;
  try {
    const u = new URL(trimmed);
    const m = (u.pathname || '').match(/track\/([^/]+)$/);
    if (m) code = decodeURIComponent(m[1]);
    else if (u.searchParams.get('code')) code = u.searchParams.get('code');
  } catch (e) { /* not a URL */ }
  return code;
}
